import { Injectable } from '@angular/core';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ChecklistFindInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-find.dto';
import type { ProjectPageInterface } from '@zaoblako/shared/modules/project/dto/project/project-page.dto';
import type { ProjectDealQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-deal-query.dto';
import type { ProjectHelpdeskTasksQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-helpdesk-tasks-query.dto';
import type { ProjectListQueryInterface } from '@zaoblako/shared/modules/project/dto/project/project-list-query.dto';
import type { ProjectPartialUpdateInterface } from '@zaoblako/shared/modules/project/dto/project/project-partial-update.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { SubprojectDefaultsFindInterface } from '@zaoblako/shared/modules/project/dto/subproject-defaults/subproject-defaults-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { ProjectChecklistService } from '@zaoblako/web/core/services/project/project-checklist.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { ProjectState } from '@zaoblako/web/modules/project/components/project/card/models/project-state';
import { projectInitialState } from '@zaoblako/web/modules/project/components/project/card/state/project-initial-state';
import { BehaviorSubject, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectStoreService extends StoreService<ProjectState> {
  protected state = new BehaviorSubject(projectInitialState);

  public get getState() {
    return this.state.value;
  }

  public get getProject(): ProjectFindInterface {
    return this.getState.project;
  }

  public constructor(
    private readonly projectService: ProjectService,
    private readonly projectChecklistService: ProjectChecklistService
  ) {
    super();
  }

  public setEntities(entities: {
    userGroups: UserGroupFindInterface[];
    directions: DirectionFindInterface[];
    clients: ClientFindInterface[];
  }) {
    this.updateState({
      userGroups: entities.userGroups,
      directions: entities.directions,
      clients: entities.clients
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any) {
    const info = this.getState.panelsInfo;
    info[key] = value;
    this.updateState({ panelsInfo: info });
    localStorage.setItem('project.panelsInfo', JSON.stringify(info));
  }

  public loadPanelsInfo() {
    if (localStorage.getItem('project.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('project.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public updateFieldProject(key: keyof ProjectPartialUpdateInterface, value: any) {
    this.projectService
      .patch({ id: this.getProject?.id, [key]: value || null })
      .pipe(tap(() => key === 'parent' && this.loadBreadcrumbs(this.getProject?.id)))
      .subscribe();
    const project: ProjectFindInterface = { ...this.getProject, [key]: value || null };
    this.updateState({ project });
  }

  public addChecklist(checklist: ChecklistFindInterface) {
    let checklists = this.getState.checklistsInfo.checklists;
    checklists.push(checklist);
    checklists = [...checklists];
    this.updateState({
      checklistsInfo: {
        checklists,
        loading: this.getState.checklistsInfo?.loading
      }
    });
  }

  public setState(state: ProjectState) {
    this.updateState(state);
  }

  public updateChecklist(checklist: ChecklistFindInterface) {
    const checklists = this.getState.checklistsInfo.checklists.filter((_cl) => _cl.id !== checklist.id);
    checklists.push(checklist);
    this.updateState({
      checklistsInfo: {
        checklists,
        loading: this.getState.checklistsInfo?.loading
      }
    });
  }

  public deleteChecklist(checklistId: number) {
    const checklists = this.getState.checklistsInfo.checklists.filter((checklist) => checklist.id !== checklistId);
    this.updateState({
      checklistsInfo: {
        checklists,
        loading: this.getState.checklistsInfo?.loading
      }
    });
  }

  public loadSubprojects(queryParams: ProjectListQueryInterface) {
    this.updateState({
      subprojectsInfo: {
        count: this.getState.subprojectsInfo.count,
        projects: this.getState.subprojectsInfo.projects,
        queryParams,
        loading: true,
        paginationLoading: this.getState.subprojectsInfo.paginationLoading
      }
    });

    let projectsMethod: Observable<ProjectPageInterface>;
    if (this.getState.subprojectsInfo?.queryParams?.listStyle === 'hierarchy') {
      projectsMethod = this.projectService.subprojectHierarchy(
        this.getProject?.id,
        this.getState.subprojectsInfo?.queryParams
      );
    } else if (this.getState.subprojectsInfo?.queryParams?.listStyle === 'flat') {
      projectsMethod = this.projectService.subprojectFlat(
        this.getProject?.id,
        this.getState.subprojectsInfo?.queryParams
      );
    }

    projectsMethod.subscribe({
      next: (data) => {
        this.updateState({
          subprojectsInfo: {
            count: data?.count,
            projects: data?.items,
            queryParams: this.getState.subprojectsInfo?.queryParams,
            loading: false,
            paginationLoading: this.getState.subprojectsInfo?.paginationLoading
          }
        });
      }
    });
  }

  public onScrollEndSubprojects() {
    let subprojectsPanel = this.getState.subprojectsInfo;
    if (
      subprojectsPanel?.projects?.length < subprojectsPanel?.count &&
      !subprojectsPanel?.loading &&
      !subprojectsPanel?.paginationLoading
    ) {
      this.updateState({
        subprojectsInfo: {
          loading: subprojectsPanel.loading,
          count: subprojectsPanel.count,
          projects: subprojectsPanel.projects,
          queryParams: {
            ...subprojectsPanel.queryParams,
            offset: subprojectsPanel.queryParams.offset + subprojectsPanel.queryParams.limit
          },
          paginationLoading: true
        }
      });

      let projectsMethod: Observable<ProjectPageInterface>;
      if (subprojectsPanel.queryParams?.listStyle === 'hierarchy') {
        projectsMethod = this.projectService.subprojectHierarchy(this.getProject?.id, subprojectsPanel.queryParams);
      } else if (subprojectsPanel.queryParams?.listStyle === 'flat') {
        projectsMethod = this.projectService.subprojectFlat(this.getProject?.id, subprojectsPanel.queryParams);
      }

      projectsMethod.subscribe({
        next: (data) => {
          subprojectsPanel = this.getState.subprojectsInfo;
          this.updateState({
            subprojectsInfo: {
              count: data?.count,
              projects: subprojectsPanel.projects.concat(data.items),
              queryParams: subprojectsPanel.queryParams,
              loading: subprojectsPanel.loading,
              paginationLoading: false
            }
          });
        }
      });
    }
  }

  public loadHelpdeskTasks(queryParams: ProjectHelpdeskTasksQueryInterface) {
    this.updateState({
      helpdeskTasksInfo: {
        tasks: this.getState.helpdeskTasksInfo.tasks,
        queryParams,
        loading: true
      }
    });

    this.projectService
      .getHelpdeskTasks(this.getState.project?.id, this.getState.helpdeskTasksInfo.queryParams)
      .subscribe({
        next: (tasks) => {
          this.updateState({
            helpdeskTasksInfo: {
              tasks,
              queryParams: this.getState.helpdeskTasksInfo.queryParams,
              loading: false
            }
          });
        }
      });
  }

  public loadChecklists() {
    this.updateState({
      checklistsInfo: {
        checklists: this.state.value.checklistsInfo.checklists,
        loading: true
      }
    });

    this.projectChecklistService.getChecklistsByProjectId(this.getState.project?.id).subscribe({
      next: (checklists) => {
        this.updateState({
          checklistsInfo: {
            checklists,
            loading: false
          }
        });
      },
      error: () => {
        this.updateState({
          checklistsInfo: {
            checklists: [],
            loading: false
          }
        });
      }
    });
  }

  public loadFiles() {
    this.updateState({
      filesInfo: {
        files: this.getState.filesInfo.files,
        loading: true
      }
    });

    this.projectService.getFiles(this.getState.project?.id).subscribe({
      next: (files) => {
        this.updateState({
          filesInfo: {
            files,
            loading: false
          }
        });
      }
    });
  }

  public loadDeals(queryParams: ProjectDealQueryInterface) {
    this.updateState({
      dealsInfo: {
        deals: this.getState.dealsInfo.deals,
        queryParams,
        loading: true
      }
    });

    this.projectService.getDeals(this.getState.project?.id, this.getState.dealsInfo.queryParams).subscribe({
      next: (deals) => {
        this.updateState({
          dealsInfo: {
            deals,
            queryParams: this.getState.dealsInfo.queryParams,
            loading: false
          }
        });
      }
    });
  }

  public getFactTime() {
    this.projectService.getFactTime(this.getState.project?.id).subscribe({
      next: (data) => {
        const project: ProjectFindInterface = { ...this.getState.project };
        project.factMin = data.factMin;
        this.updateState({ project });
      }
    });
  }

  public loadDefaults() {
    this.updateState({
      subprojectDefaults: {
        defaults: this.getState.subprojectDefaults.defaults,
        loading: true
      }
    });

    this.projectService.getSubprojectDefaults(this.getProject?.id).subscribe({
      next: (defaults) => {
        this.updateState({
          subprojectDefaults: {
            defaults,
            loading: false
          }
        });
      }
    });
  }

  public updateDefaults(defaults: SubprojectDefaultsFindInterface) {
    this.updateState({
      subprojectDefaults: {
        defaults,
        loading: this.getState.checklistsInfo?.loading
      }
    });
  }

  public updateFilesCount(count: number) {
    this.updateState((state) => ({
      project: {
        ...state.project,
        filesCount: state.project.filesCount + count
      }
    }));
  }

  public loadBreadcrumbs(projectId: number) {
    this.projectService.breadcrumbs(projectId).subscribe({
      next: (projects) => {
        const projectBreadcrumbs = projects.map((project) => {
          return {
            label: project.name,
            routerLink: `/project/card/${project.id}`
          };
        });
        this.updateState({
          projectBreadcrumbs
        });
      }
    });
  }
}
